import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, ImagePlus, MapPin, Save, Search, Trash2, Upload, RefreshCw } from "lucide-react";
import { api } from "../../services/api";
import { resolveProductImage } from "../../utils/productAssets";
import { ToastStack, useToast } from "./useToast";
import RichTextEditor from "../../components/RichTextEditor";
import MediaLibraryPicker from "../../components/media/MediaLibraryPicker";

const emptyProduct = {
  name: "",
  sku: "",
  brandLine: "Juki",
  category: "",
  categorySlug: "",
  subcategory: "",
  subcategorySlug: "",
  categoryAssignments: [],
  image: "",
  images: [],
  shortDescription: "",
  description: "",
  condition: "New",
  manufacturerPartNumber: "",
  price: "",
  compareAtPrice: "",
  cost: "",
  stock: "",
  lowStockThreshold: 10,
  weightLb: "",
  storefrontSection: "featured",
  status: "active",
  ebayCategoryId: "",
  ebayCategoryPath: "",
  ebayPrice: "",
  warehouseLocations: [],
  variantAttributes: [],
  variants: [],
};

const productStatusOptions = [
  {
    value: "active",
    label: "Active",
    description: "Visible in the storefront and ready for selling.",
    Icon: CheckCircle2,
    activeClass: "border-emerald-200 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-100",
    iconClass: "bg-emerald-100 text-emerald-700",
  },
  {
    value: "inactive",
    label: "Inactive",
    description: "Kept in admin, hidden from customer browsing.",
    Icon: AlertTriangle,
    activeClass: "border-amber-200 bg-amber-50 text-amber-800 ring-2 ring-amber-100",
    iconClass: "bg-amber-100 text-amber-700",
  },
  {
    value: "draft",
    label: "Draft",
    description: "Saved for review before publishing.",
    Icon: FileText,
    activeClass: "border-blue-200 bg-blue-50 text-blue-800 ring-2 ring-blue-100",
    iconClass: "bg-blue-100 text-blue-700",
  },
];

function numberOrUndefined(value) {
  return value === "" || value === null || value === undefined ? undefined : Number(value);
}

function normalizeVariantAttributes(input = []) {
  if (!Array.isArray(input)) return [];
  return input.map((attribute) => ({
    name: attribute.name || "",
    values: Array.isArray(attribute.values) ? attribute.values : [],
    visible: attribute.visible !== false,
    variation: attribute.variation !== false,
  }));
}

function variantAttributesObject(variant = {}) {
  return variant.attributes || (variant.optionName && variant.optionValue ? { [variant.optionName]: variant.optionValue } : {});
}

function variantNameFromAttributes(attributes = {}) {
  return Object.entries(attributes)
    .filter(([, value]) => value)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" / ");
}

function normalizeVariants(input = [], product = {}) {
  if (!Array.isArray(input)) return [];
  return input.map((variant) => {
    const attributes = variantAttributesObject(variant);
    const variantPrice = Number(variant.price || 0);
    return {
      _id: variant._id,
      name: variant.name || variantNameFromAttributes(attributes),
      sku: variant.sku || "",
      attributes,
      image: variant.image || product.image || "",
      price: variantPrice > 0 ? variant.price : product.price ?? "",
      compareAtPrice: variant.compareAtPrice ?? "",
      stock: variant.stock ?? 0,
      weightLb: variant.weightLb ?? product.weightLb ?? "",
      enabled: variant.enabled !== false,
    };
  });
}

function buildVariantCombinations(attributes = []) {
  const usableAttributes = attributes
    .map((attribute) => ({
      name: String(attribute.name || "").trim(),
      values: (attribute.values || []).map((value) => String(value || "").trim()).filter(Boolean),
    }))
    .filter((attribute) => attribute.name && attribute.values.length);

  if (!usableAttributes.length) return [];

  return usableAttributes.reduce(
    (combinations, attribute) =>
      combinations.flatMap((combination) =>
        attribute.values.map((value) => ({
          ...combination,
          [attribute.name]: value,
        }))
      ),
    [{}]
  );
}

function warehouseAssignmentStateFromProduct(product = {}) {
  const assignments = {};

  (product.warehouseLocations || []).forEach((location) => {
    const warehouseId = String(location.warehouse || location.warehouseId || "").trim();
    if (!warehouseId) return;

    assignments[warehouseId] = {
      selected: true,
      bin: location.bin || "Primary",
      quantity: location.quantity ?? 0,
      reorderPoint: location.reorderPoint ?? 0,
      atumLocationId: location.atumLocationId || "",
      atumLocationSlug: location.atumLocationSlug || "",
    };
  });

  return assignments;
}

function normalizeCategoryAssignments(product = {}) {
  if (Array.isArray(product.categoryAssignments) && product.categoryAssignments.length) {
    return product.categoryAssignments
      .map((assignment) => ({
        category: assignment.category || "",
        categorySlug: assignment.categorySlug || "",
        subcategory: assignment.subcategory || "",
        subcategorySlug: assignment.subcategorySlug || "",
        categoryId: assignment.categoryId,
        subcategoryId: assignment.subcategoryId,
        path: Array.isArray(assignment.path) ? assignment.path : [assignment.categorySlug, assignment.subcategorySlug].filter(Boolean),
      }))
      .filter((assignment) => assignment.categorySlug);
  }

  if (!product.categorySlug && !product.category) return [];

  return [{
    category: product.category || "",
    categorySlug: product.categorySlug || "",
    subcategory: product.subcategory || "",
    subcategorySlug: product.subcategorySlug || "",
    path: [product.categorySlug, product.subcategorySlug].filter(Boolean),
  }];
}

export default function ProductForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(productId);
  const [form, setForm] = useState(emptyProduct);
  const [categories, setCategories] = useState([]);
  const [ebayCategorySearch, setEbayCategorySearch] = useState("");
  const [ebayCategorySuggestions, setEbayCategorySuggestions] = useState([]);
  const [ebayCategoryLoading, setEbayCategoryLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseAssignments, setWarehouseAssignments] = useState({});
  const [warehouseAssignmentsTouched, setWarehouseAssignmentsTouched] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [categoryDraft, setCategoryDraft] = useState({ categorySlug: "", subcategorySlug: "" });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [ebayUploading, setEbayUploading] = useState(false);
  const [ebayChannel, setEbayChannel] = useState(null);
  const { dismiss, toast, toasts } = useToast();

  useEffect(() => {
    let mounted = true;
    setWarehouseLoading(true);
    Promise.allSettled([api.get("/categories"), api.get("/warehouses")])
      .then(([categoryResult, warehouseResult]) => {
        if (!mounted) return;
        setCategories(categoryResult.status === "fulfilled" ? categoryResult.value.categories || [] : []);

        const nextWarehouses = warehouseResult.status === "fulfilled" ? warehouseResult.value.warehouses || [] : [];
        setWarehouses(nextWarehouses);
      })
      .finally(() => {
        if (mounted) setWarehouseLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    api.get(`/products/${productId}`)
      .then((data) => {
        if (!mounted) return;
        const product = data.product;
        const existingEbayChannel = (product.channels || []).find(
          (channel) => channel?.channel === "ebay"
        ) || null;
        setEbayChannel(existingEbayChannel);
        setForm({
          ...emptyProduct,
          name: product.name || "",
          sku: product.sku || "",
          brandLine: product.brandLine || "Juki",
          category: product.category || "",
          categorySlug: product.categorySlug || "",
          subcategory: product.subcategory || "",
          subcategorySlug: product.subcategorySlug || "",
          categoryAssignments: normalizeCategoryAssignments(product),
          image: product.image || "",
          images: product.images || [],
          shortDescription: product.shortDescription || "",
          description: product.description || "",
          condition: product.condition || "New",
          manufacturerPartNumber: product.manufacturerPartNumber || product.sku || "",
          price: product.price ?? "",
          compareAtPrice: product.compareAtPrice ?? "",
          cost: product.cost ?? "",
          stock: product.stock ?? "",
          lowStockThreshold: product.lowStockThreshold ?? 10,
          weightLb: product.weightLb ?? "",
          storefrontSection: product.storefrontSection || "featured",
          status: product.status || "active",
          ebayCategoryId: product.ebay?.categoryId || "",
          ebayCategoryPath: product.ebay?.categoryPath || "",
          ebayPrice: product.ebay?.price ?? product.price ?? "",
          warehouseLocations: product.warehouseLocations || [],
          variantAttributes: normalizeVariantAttributes(product.variantAttributes),
          variants: normalizeVariants(product.variants, product),
        });
        setWarehouseAssignments(warehouseAssignmentStateFromProduct(product));
        setWarehouseAssignmentsTouched(false);
      })
      .catch((err) => toast(err.message, "error"))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isEdit, productId, toast]);

  const sliderImages = useMemo(() => {
    const combined = [form.image, ...(form.images || [])].filter(Boolean);
    return [...new Set(combined)];
  }, [form.image, form.images]);

  const visibleWarehouses = useMemo(() => {
    const query = warehouseSearch.trim().toLowerCase();

    if (!query) return warehouses;

    return warehouses.filter((warehouse) =>
      [warehouse.name, warehouse.code, warehouse.region, warehouse.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [warehouseSearch, warehouses]);

  const selectedWarehouseAssignments = useMemo(() => {
    return warehouses
      .map((warehouse) => {
        const assignment = warehouseAssignments[warehouse._id];

        if (!assignment?.selected) return null;

        return {
          warehouse: warehouse._id,
          warehouseId: warehouse._id,
          warehouseName: warehouse.name,
          warehouseCode: warehouse.code,
          atumLocationId: assignment.atumLocationId || undefined,
          atumLocationSlug: assignment.atumLocationSlug || undefined,
          bin: assignment.bin || warehouse.code || "Primary",
          quantity: Math.max(0, Number(assignment.quantity || 0)),
          reorderPoint: Math.max(0, Number(assignment.reorderPoint || 0)),
        };
      })
      .filter(Boolean);
  }, [warehouseAssignments, warehouses]);

  const productWarehouseRows = selectedWarehouseAssignments;

  const selectedDraftCategory = useMemo(
    () => categories.find((category) => category.slug === categoryDraft.categorySlug),
    [categories, categoryDraft.categorySlug]
  );

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addVariantAttribute = () => {
    setForm((current) => ({
      ...current,
      variantAttributes: [
        ...(current.variantAttributes || []),
        { name: "", values: [], visible: true, variation: true },
      ],
    }));
  };

  const updateVariantAttribute = (index, field, value) => {
    setForm((current) => {
      const nextAttributes = [...(current.variantAttributes || [])];
      nextAttributes[index] = {
        ...(nextAttributes[index] || {}),
        [field]: field === "values"
          ? String(value || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : value,
      };
      return { ...current, variantAttributes: nextAttributes };
    });
  };

  const removeVariantAttribute = (index) => {
    setForm((current) => ({
      ...current,
      variantAttributes: (current.variantAttributes || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const generateVariants = () => {
    const combinations = buildVariantCombinations(form.variantAttributes);
    if (!combinations.length) {
      toast("Add at least one variation attribute and values first.", "warning");
      return;
    }

    setForm((current) => {
      const existing = new Map(
        (current.variants || []).map((variant) => [
          JSON.stringify(variantAttributesObject(variant)),
          variant,
        ])
      );

      const nextVariants = combinations.map((attributes) => {
        const key = JSON.stringify(attributes);
        const existingVariant = existing.get(key);
        const existingPrice = Number(existingVariant?.price || 0);
        return {
          ...(existingVariant || {}),
          attributes,
          name: existingVariant?.name || variantNameFromAttributes(attributes),
          sku: existingVariant?.sku || `${current.sku}-${Object.values(attributes).join("-")}`.replace(/[^a-zA-Z0-9_-]+/g, "-"),
          price: existingPrice > 0 ? existingVariant.price : current.price,
          compareAtPrice: existingVariant?.compareAtPrice ?? "",
          stock: existingVariant?.stock ?? 0,
          weightLb: existingVariant?.weightLb ?? current.weightLb,
          image: existingVariant?.image || current.image,
          enabled: existingVariant?.enabled !== false,
        };
      });

      return { ...current, variants: nextVariants };
    });

    toast(`Generated ${combinations.length} variation${combinations.length === 1 ? "" : "s"}.`, "success");
  };

  const updateVariant = (index, field, value) => {
    setForm((current) => {
      const variants = [...(current.variants || [])];
      variants[index] = { ...(variants[index] || {}), [field]: value };
      return { ...current, variants };
    });
  };

  const removeVariant = (index) => {
    setForm((current) => ({
      ...current,
      variants: (current.variants || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const toggleWarehouseAssignment = (warehouse) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => {
      const existing = current[warehouse._id] || {};
      const selected = !existing.selected;
      const selectedCount = Object.values(current).filter((item) => item?.selected).length;

      return {
        ...current,
        [warehouse._id]: {
          ...existing,
          selected,
          bin: existing.bin || warehouse.code || "Primary",
          quantity:
            existing.quantity ??
            (selectedCount === 0 ? Number(form.stock || 0) : 0),
          reorderPoint: existing.reorderPoint ?? Number(form.lowStockThreshold || 0),
        },
      };
    });
  };

  const updateWarehouseAssignment = (warehouseId, field, value) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => ({
      ...current,
      [warehouseId]: {
        ...(current[warehouseId] || {}),
        selected: true,
        [field]: value,
      },
    }));
  };

  const removeWarehouseAssignment = (warehouseId) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => {
      const existing = current[warehouseId] || {};
      return {
        ...current,
        [warehouseId]: {
          ...existing,
          selected: false,
        },
      };
    });
  };

  const addCategoryAssignment = () => {
    const category = categories.find((item) => item.slug === categoryDraft.categorySlug);
    if (!category) {
      toast("Select a category first.", "warning");
      return;
    }

    const subcategory = (category.subcategories || []).find((item) => item.slug === categoryDraft.subcategorySlug);
    const assignment = {
      category: category.name || "",
      categorySlug: category.slug || "",
      subcategory: subcategory?.name || "",
      subcategorySlug: subcategory?.slug || "",
      categoryId: category.wcId || category.id,
      subcategoryId: subcategory?.wcId || subcategory?.id,
      path: [category.slug, subcategory?.slug].filter(Boolean),
    };

    setForm((current) => {
      const exists = (current.categoryAssignments || []).some(
        (item) =>
          item.categorySlug === assignment.categorySlug &&
          (item.subcategorySlug || "") === (assignment.subcategorySlug || "")
      );

      if (exists) return current;

      const nextAssignments = [...(current.categoryAssignments || []), assignment];
      const primary = nextAssignments[0] || {};

      return {
        ...current,
        categoryAssignments: nextAssignments,
        category: primary.category || "",
        categorySlug: primary.categorySlug || "",
        subcategory: primary.subcategory || "",
        subcategorySlug: primary.subcategorySlug || "",
      };
    });

    setCategoryDraft({ categorySlug: "", subcategorySlug: "" });
  };

  const removeCategoryAssignment = (index) => {
    setForm((current) => ({
      ...current,
      categoryAssignments: (current.categoryAssignments || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  useEffect(() => {
    const primary = (form.categoryAssignments || [])[0] || {};
    setForm((current) => ({
      ...current,
      category: primary.category || "",
      categorySlug: primary.categorySlug || "",
      subcategory: primary.subcategory || "",
      subcategorySlug: primary.subcategorySlug || "",
    }));
  }, [form.categoryAssignments]);

  const selectMainImage = (image) => {
    setForm((current) => ({
      ...current,
      image,
      images: [...new Set([image, ...(current.images || [])])],
    }));
    toast("Main image selected", "success");
  };

  const selectGalleryImages = (images) => {
    const selectedImages = Array.isArray(images) ? images : [images].filter(Boolean);
    if (!selectedImages.length) return;
    setForm((current) => ({
      ...current,
      image: current.image || selectedImages[0],
      images: [...new Set([...(current.images || []), ...selectedImages])],
    }));
    toast(`Added ${selectedImages.length} slider image${selectedImages.length === 1 ? "" : "s"}`, "success");
  };

  const removeImage = (image) => {
    setForm((current) => {
      const nextImages = (current.images || []).filter((item) => item !== image);
      const nextMain = current.image === image ? nextImages[0] || "" : current.image;
      return { ...current, image: nextMain, images: nextImages };
    });
  };

  const makeMainImage = (image) => {
    setForm((current) => ({
      ...current,
      image,
      images: [...new Set([image, ...(current.images || [])])],
    }));
  };

  const searchEbayCategories = async () => {
    const query = ebayCategorySearch.trim() || form.name || form.category || form.brandLine;

    if (!query || query.length < 2) {
      return toast("Enter at least 2 characters to search eBay categories", "warning");
    }

    setEbayCategoryLoading(true);

    try {
      const data = await api.get(`/channels/ebay/category-suggestions?q=${encodeURIComponent(query)}`);
      setEbayCategorySuggestions(data.suggestions || []);

      if (!data.suggestions?.length) {
        toast("No eBay category suggestions found", "warning");
      }
    } catch (err) {
      toast(err.message, "error");
      setEbayCategorySuggestions([]);
    } finally {
      setEbayCategoryLoading(false);
    }
  };

  const selectEbayCategory = (suggestion) => {
    setForm((current) => ({
      ...current,
      ebayCategoryId: suggestion.categoryId || "",
      ebayCategoryPath: (suggestion.path || []).join(" > "),
    }));
    setEbayCategorySuggestions([]);
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        images: [...new Set([form.image, ...(form.images || [])].filter(Boolean))],
        price: Number(form.price),
        compareAtPrice: numberOrUndefined(form.compareAtPrice),
        cost: Number(form.cost || 0),
        stock: Number(form.stock || 0),
        lowStockThreshold: Number(form.lowStockThreshold || 10),
        weightLb: Number(form.weightLb || 0),
        categoryAssignments: form.categoryAssignments || [],
        warehouseLocations: selectedWarehouseAssignments,
        variantAttributes: normalizeVariantAttributes(form.variantAttributes),
        variants: normalizeVariants(form.variants, form),
        channels: [
          { channel: "bobbinusa", status: form.status === "active" ? "active" : "draft" },
          {
            channel: "ebay",
            status: ebayChannel?.status || "draft",
            externalId: ebayChannel?.externalId,
            listingUrl: ebayChannel?.listingUrl,
            lastSyncedAt: ebayChannel?.lastSyncedAt,
          },
        ],
        ebay: {
          enabled: form.status === "active" || ebayChannel?.status === "active",
          price: numberOrUndefined(form.ebayPrice),
          title: form.name.slice(0, 80),
          condition: "NEW",
          categoryId: form.ebayCategoryId,
          categoryPath: form.ebayCategoryPath,
          aspects: { Brand: form.brandLine, Type: form.category, Condition: form.condition },
          listingDescription: form.description || form.shortDescription,
        },
      };
      const data = isEdit
        ? await api.patch(`/products/${productId}`, payload)
        : await api.post("/products", payload);

      if (
        data.product?._id &&
        (warehouseAssignmentsTouched || selectedWarehouseAssignments.length > 0)
      ) {
        await api.post(`/products/${data.product._id}/warehouses`, {
          warehouseLocations: selectedWarehouseAssignments,
          updateStockFromWarehouses: selectedWarehouseAssignments.length > 0,
        });
      }

      toast(`${isEdit ? "Updated" : "Created"} product ${data.product.sku}`, "success");
      if (data.ebayPriceSync?.success) {
        toast("eBay price updated", "success");
      } else if (data.ebayPriceSync?.message) {
        toast(data.ebayPriceSync.message, "warning");
        return;
      }
      navigate("/dashboard/products");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const uploadToEbay = async () => {
    if (!isEdit || ebayUploading) return;

    setEbayUploading(true);
    try {
      const data = await api.post("/channels/ebay/products/upload", {
        productId,
      });
      const nextChannel = {
        ...(ebayChannel || {}),
        channel: "ebay",
        status: "active",
        externalId: data.listingId || ebayChannel?.externalId,
        listingUrl: data.listingUrl || ebayChannel?.listingUrl,
      };
      setEbayChannel(nextChannel);
      toast(data.message || "Product uploaded to eBay", "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setEbayUploading(false);
    }
  };

  if (loading) {
    return <div className="p-6 md:p-10 text-sm font-semibold text-gray-500">Loading product...</div>;
  }

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Catalog Management</p>
          <h1 className="text-3xl font-black text-gray-900">{isEdit ? "Edit Product" : "Add Product"}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Edit catalog details, eBay listing data, warehouse locations, and images from one product workspace.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-sm font-black text-white disabled:opacity-60"
            disabled={saving}
            form="product-edit-form"
            type="submit"
          >
            <Save size={16} /> {saving ? "Saving..." : "Save Product"}
          </button>
          {isEdit && (
            <Link
              className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
              target="_blank"
              rel="noreferrer"
              to={`/admin-preview/products/${productId}`}
            >
              Preview Product
            </Link>
          )}
          {isEdit && (
            <>
              <button
                className="inline-flex items-center gap-2 rounded-xl border border-[#e53238] bg-white px-4 py-3 text-sm font-black text-[#e53238] hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={ebayUploading || saving}
                onClick={uploadToEbay}
                type="button"
              >
                {ebayUploading ? <RefreshCw className="animate-spin" size={16} /> : <Upload size={16} />}
                {ebayUploading ? "Uploading..." : "Upload to eBay"}
              </button>
              <a
                aria-disabled={!ebayChannel?.listingUrl}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-black ${
                  ebayChannel?.listingUrl
                    ? "border border-gray-200 bg-white text-gray-700 hover:border-[#e53238] hover:text-[#e53238]"
                    : "cursor-not-allowed border border-gray-100 bg-gray-50 text-gray-400"
                }`}
                href={ebayChannel?.listingUrl || undefined}
                onClick={(event) => {
                  if (!ebayChannel?.listingUrl) event.preventDefault();
                }}
                rel="noreferrer"
                target="_blank"
              >
                <ExternalLink size={16} /> View on eBay
              </a>
            </>
          )}
          <Link className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-600" to="/dashboard/products">
            Back to products
          </Link>
        </div>
      </div>

      <form className="grid gap-6 xl:grid-cols-[1fr_420px]" id="product-edit-form" onSubmit={saveProduct}>
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-5 text-lg font-black text-gray-900">Product Details</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["name", "Product name", "text", true],
              ["sku", "SKU", "text", true],
              ["brandLine", "Brand line", "text", false],
              ["manufacturerPartNumber", "Manufacturer part number", "text", false],
              ["condition", "Condition", "text", false],
              ["price", "Price", "number", true],
              ["compareAtPrice", "Compare at price", "number", false],
              ["cost", "Cost", "number", false],
              ["stock", "Stock", "number", false],
              ["lowStockThreshold", "Low stock threshold", "number", false],
              ["weightLb", "Weight lb", "number", false],
            ].map(([name, label, type, required]) => (
              <label className="block" key={name}>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                <input
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                  name={name}
                  onChange={updateForm}
                  required={required}
                  step={type === "number" ? "0.01" : undefined}
                  type={type}
                  value={form[name]}
                />
              </label>
            ))}
            <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="block flex-1">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Website category</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    onChange={(event) => setCategoryDraft({ categorySlug: event.target.value, subcategorySlug: "" })}
                    value={categoryDraft.categorySlug}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => <option key={category.slug} value={category.slug}>{category.name}</option>)}
                  </select>
                </label>
                <label className="block flex-1">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Subcategory</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] disabled:bg-gray-100 disabled:text-gray-400"
                    disabled={!categoryDraft.categorySlug}
                    onChange={(event) => setCategoryDraft((current) => ({ ...current, subcategorySlug: event.target.value }))}
                    value={categoryDraft.subcategorySlug}
                  >
                    <option value="">No subcategory</option>
                    {(selectedDraftCategory?.subcategories || []).map((subcategory) => (
                      <option key={subcategory.slug} value={subcategory.slug}>{subcategory.name}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white transition hover:bg-[#347fba]"
                  onClick={addCategoryAssignment}
                  type="button"
                >
                  Add category
                </button>
              </div>

              <div className="mt-4">
                <p className="text-xs font-black uppercase text-gray-500">Assigned categories</p>
                <div className="mt-2 grid gap-2">
                  {(form.categoryAssignments || []).length ? (
                    form.categoryAssignments.map((assignment, index) => (
                      <div
                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2"
                        key={`${assignment.categorySlug}-${assignment.subcategorySlug || "root"}-${index}`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-gray-900">
                            {assignment.category || assignment.categorySlug}
                          </p>
                          {assignment.subcategory || assignment.subcategorySlug ? (
                            <p className="truncate text-xs font-bold text-gray-500">
                              {assignment.subcategory || assignment.subcategorySlug}
                            </p>
                          ) : (
                            <p className="truncate text-xs font-bold text-gray-400">Root category</p>
                          )}
                        </div>
                        <button
                          aria-label={`Remove ${assignment.subcategory || assignment.category} category assignment`}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-100 text-red-500 transition hover:border-red-200 hover:bg-red-50"
                          onClick={() => removeCategoryAssignment(index)}
                          title="Remove category"
                          type="button"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="rounded-xl bg-white p-3 text-xs font-bold text-gray-400">
                      No website categories assigned yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Website section</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="storefrontSection" onChange={updateForm} value={form.storefrontSection}>
                <option value="featured">Featured</option>
                <option value="popular">Popular</option>
                <option value="mostViewed">Most viewed</option>
                <option value="topSelling">Top selling</option>
                <option value="machines">Machines</option>
              </select>
            </label>
            <div className="md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Status</span>
              <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Product status">
                {productStatusOptions.map(({ value, label, description, Icon, activeClass, iconClass }) => {
                  const selected = form.status === value;

                  return (
                    <button
                      aria-checked={selected}
                      className={`flex min-h-[104px] items-start gap-3 rounded-xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-[#4391d1]/30 ${
                        selected
                          ? activeClass
                          : "border-gray-200 bg-gray-50 text-gray-700 hover:border-[#4391d1] hover:bg-white"
                      }`}
                      key={value}
                      onClick={() => setForm((current) => ({ ...current, status: value }))}
                      role="radio"
                      type="button"
                    >
                      <span className={`mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg ${selected ? iconClass : "bg-white text-gray-400"}`}>
                        <Icon size={17} />
                      </span>
                      <span>
                        <span className="block text-sm font-black">{label}</span>
                        <span className={`mt-1 block text-xs font-semibold leading-5 ${selected ? "" : "text-gray-500"}`}>
                          {description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <div className="mb-4">
                <h3 className="text-sm font-black text-blue-950">eBay Listing</h3>
                <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
                  Search once, pick a category, set the eBay price, then save this product.
                </p>
              </div>
              <div className="grid gap-3 lg:grid-cols-[1.5fr_auto] lg:items-end">
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">Search eBay category</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    onChange={(event) => setEbayCategorySearch(event.target.value)}
                    placeholder="Sewing machine parts"
                    value={ebayCategorySearch}
                  />
                </label>
                <button
                  className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white disabled:opacity-60"
                  disabled={ebayCategoryLoading}
                  onClick={searchEbayCategories}
                  type="button"
                >
                  {ebayCategoryLoading ? "Searching..." : "Find Category"}
                </button>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">Selected eBay category ID</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    name="ebayCategoryId"
                    onChange={updateForm}
                    placeholder="Pick from search or paste an ID"
                    value={form.ebayCategoryId}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">eBay price</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    min="0"
                    name="ebayPrice"
                    onChange={updateForm}
                    placeholder="Blank uses website price"
                    step="0.01"
                    type="number"
                    value={form.ebayPrice}
                  />
                </label>
              </div>
              {form.ebayCategoryPath && (
                <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-bold text-blue-800">
                  Selected path: {form.ebayCategoryPath}
                </p>
              )}
              {ebayCategorySuggestions.length > 0 && (
                <div className="mt-3 grid gap-2">
                  {ebayCategorySuggestions.slice(0, 6).map((suggestion) => (
                    <button
                      className="rounded-xl border border-blue-100 bg-white px-4 py-3 text-left text-xs font-bold text-gray-700 hover:border-[#4391d1]"
                      key={suggestion.categoryId}
                      onClick={() => selectEbayCategory(suggestion)}
                      type="button"
                    >
                      <span className="block font-black text-gray-900">
                        {suggestion.categoryName} ({suggestion.categoryId})
                      </span>
                      <span className="mt-1 block text-gray-500">
                        {(suggestion.path || []).join(" > ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs font-semibold leading-5 text-blue-800">
                Website categories are separate from eBay categories. When editing, changing eBay price also updates the existing eBay offer.
              </p>
            </div>
            <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-sm font-black text-gray-950">Product Variations</h3>
                  <p className="mt-1 text-xs font-semibold leading-5 text-gray-500">
                    Add options such as Size, Color, Power, or Gauge, then generate variation rows with their own SKU, price, and stock.
                  </p>
                </div>
                <button
                  className="rounded-xl bg-gray-950 px-4 py-2 text-xs font-black text-white"
                  onClick={addVariantAttribute}
                  type="button"
                >
                  Add Option
                </button>
              </div>

              <div className="mt-4 grid gap-3">
                {(form.variantAttributes || []).map((attribute, index) => (
                  <div className="grid gap-3 rounded-xl border border-gray-100 bg-white p-3 md:grid-cols-[180px_1fr_auto]" key={`variant-attribute-${index}`}>
                    <label>
                      <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">Option name</span>
                      <input
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                        onChange={(event) => updateVariantAttribute(index, "name", event.target.value)}
                        placeholder="Size"
                        value={attribute.name || ""}
                      />
                    </label>
                    <label>
                      <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">Values</span>
                      <input
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                        onChange={(event) => updateVariantAttribute(index, "values", event.target.value)}
                        placeholder="Small, Medium, Large"
                        value={(attribute.values || []).join(", ")}
                      />
                    </label>
                    <button
                      className="self-end rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600"
                      onClick={() => removeVariantAttribute(index)}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {!form.variantAttributes?.length && (
                  <p className="rounded-xl bg-white p-4 text-xs font-bold text-gray-500">
                    No variation options yet. Add an option when this product has multiple sizes, colors, power types, gauges, or similar choices.
                  </p>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <p className="text-xs font-bold text-gray-500">
                  {(form.variants || []).length} variation{(form.variants || []).length === 1 ? "" : "s"} configured.
                </p>
                <button
                  className="rounded-xl bg-[#4391d1] px-4 py-2 text-xs font-black text-white disabled:opacity-50"
                  disabled={!form.variantAttributes?.length}
                  onClick={generateVariants}
                  type="button"
                >
                  Generate Variations
                </button>
              </div>

              {(form.variants || []).length > 0 && (
                <div className="mt-4 overflow-x-auto rounded-xl border border-gray-100 bg-white">
                  <table className="w-full min-w-[820px] text-left text-xs">
                    <thead className="bg-gray-50 text-[10px] font-black uppercase tracking-wide text-gray-400">
                      <tr>
                        <th className="px-3 py-3">Variation</th>
                        <th className="px-3 py-3">SKU</th>
                        <th className="px-3 py-3">Price</th>
                        <th className="px-3 py-3">Stock</th>
                        <th className="px-3 py-3">Image</th>
                        <th className="px-3 py-3">Active</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {form.variants.map((variant, index) => (
                        <tr key={variant._id || `variant-row-${index}`}>
                          <td className="px-3 py-3 font-black text-gray-900">
                            {variant.name || variantNameFromAttributes(variantAttributesObject(variant))}
                          </td>
                          <td className="px-3 py-3">
                            <input
                              className="w-36 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 font-bold outline-none focus:border-[#4391d1]"
                              onChange={(event) => updateVariant(index, "sku", event.target.value)}
                              value={variant.sku || ""}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              className="w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 font-bold outline-none focus:border-[#4391d1]"
                              min="0"
                              onChange={(event) => updateVariant(index, "price", event.target.value)}
                              step="0.01"
                              type="number"
                              value={variant.price ?? ""}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <input
                              className="w-20 rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 font-bold outline-none focus:border-[#4391d1]"
                              min="0"
                              onChange={(event) => updateVariant(index, "stock", event.target.value)}
                              type="number"
                              value={variant.stock ?? 0}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex min-w-[260px] items-center gap-2">
                              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
                                {variant.image ? (
                                  <img alt={variant.name || "Variation"} className="h-full w-full object-contain p-1" src={resolveProductImage(variant.image)} />
                                ) : (
                                  <ImagePlus className="h-5 w-5 text-gray-300" />
                                )}
                              </div>
                              <div className="grid flex-1 gap-1">
                                <div className="flex flex-wrap gap-1">
                                  <MediaLibraryPicker
                                    buttonClassName="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]"
                                    buttonLabel="Media"
                                    folder="products"
                                    onSelect={(image) => updateVariant(index, "image", image)}
                                    selectedUrls={variant.image ? [variant.image] : []}
                                    source="products"
                                  />
                                  <button
                                    className="rounded-lg text-xs border border-gray-200 bg-white px-2.5 py-1.5 font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1] disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={!form.image}
                                    onClick={() => updateVariant(index, "image", form.image)}
                                    type="button"
                                  >
                                    Use main
                                  </button>
                                </div>
                                {sliderImages.length > 0 && (
                                  <select
                                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 font-bold text-gray-600 outline-none focus:border-[#4391d1]"
                                    onChange={(event) => updateVariant(index, "image", event.target.value)}
                                    value={variant.image || ""}
                                  >
                                    <option value="">Choose existing image</option>
                                    {sliderImages.map((image, imageIndex) => (
                                      <option key={`${image}-${imageIndex}`} value={image}>
                                        {image === form.image ? "Main product image" : `Gallery image ${imageIndex + 1}`}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <input
                              checked={variant.enabled !== false}
                              className="h-4 w-4 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
                              onChange={(event) => updateVariant(index, "enabled", event.target.checked)}
                              type="checkbox"
                            />
                          </td>
                          <td className="px-3 py-3 text-right">
                            <button
                              className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 font-black text-red-600"
                              onClick={() => removeVariant(index)}
                              type="button"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <RichTextEditor
              label="Short description"
              minHeight="min-h-[180px]"
              value={form.shortDescription}
              onChange={(html) =>
                setForm((current) => ({
                  ...current,
                  shortDescription: html,
                }))
              }
            />
            <RichTextEditor
              label="Full description"
              value={form.description}
              onChange={(html) =>
                setForm((current) => ({
                  ...current,
                  description: html,
                }))
              }
            />
          </div>
        </section>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                <MapPin size={18} />
              </span>
              <div>
                <h2 className="text-lg font-black text-gray-900">Warehouse Locations</h2>
                <p className="mt-1 text-xs font-semibold text-gray-400">
                  Select every warehouse location where this SKU belongs.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                  onChange={(event) => setWarehouseSearch(event.target.value)}
                  placeholder="Filter warehouses (optional)"
                  value={warehouseSearch}
                />
              </div>

              <div className="mt-3 flex items-center justify-between text-xs font-black text-gray-500">
                <span>{selectedWarehouseAssignments.length} selected</span>
                <span>{visibleWarehouses.length} shown</span>
              </div>
            </div>

            <div className="mt-4 max-h-[460px] space-y-3 overflow-y-auto pr-1">
              {warehouseLoading ? (
                <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-400">
                  Loading warehouses...
                </p>
              ) : visibleWarehouses.length ? (
                visibleWarehouses.map((warehouse) => {
                  const assignment = warehouseAssignments[warehouse._id] || {};
                  const selected = Boolean(assignment.selected);

                  return (
                    <div
                      className={`rounded-xl border p-3 transition-colors ${
                        selected
                          ? "border-blue-200 bg-blue-50"
                          : "border-gray-100 bg-gray-50"
                      }`}
                      key={warehouse._id}
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          checked={selected}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
                          onChange={() => toggleWarehouseAssignment(warehouse)}
                          type="checkbox"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-black text-gray-900">
                            {warehouse.name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs font-bold text-gray-500">
                            {warehouse.code}
                            {warehouse.region ? ` - ${warehouse.region}` : ""}
                          </span>
                        </span>
                      </label>

                      {selected && (
                        <div className="mt-3 grid gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                              Bin
                            </span>
                            <input
                              className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                              onChange={(event) =>
                                updateWarehouseAssignment(
                                  warehouse._id,
                                  "bin",
                                  event.target.value
                                )
                              }
                              value={assignment.bin || ""}
                            />
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                                Qty
                              </span>
                              <input
                                className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                                min="0"
                                onChange={(event) =>
                                  updateWarehouseAssignment(
                                    warehouse._id,
                                    "quantity",
                                    event.target.value
                                  )
                                }
                                type="number"
                                value={assignment.quantity ?? 0}
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                                Reorder
                              </span>
                              <input
                                className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                                min="0"
                                onChange={(event) =>
                                  updateWarehouseAssignment(
                                    warehouse._id,
                                    "reorderPoint",
                                    event.target.value
                                  )
                                }
                                type="number"
                                value={assignment.reorderPoint ?? 0}
                              />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-400">
                  No warehouse locations found.
                </p>
              )}
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4">
              <p className="text-xs font-black uppercase text-gray-500">Current Locations</p>
              <div className="mt-3 grid gap-2">
                {productWarehouseRows.length ? (
                  productWarehouseRows.map((stock) => (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" key={`${stock.warehouseId}-${stock.bin}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-gray-900">{stock.warehouseName}</p>
                          <p className="truncate text-xs font-bold text-gray-500">Bin/Drawer: {stock.bin}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="rounded-lg bg-white px-2 py-1 text-xs font-black text-gray-700">Qty {stock.quantity}</span>
                          <button
                            aria-label={`Remove ${stock.warehouseName} from current locations`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 bg-white text-red-500 transition hover:border-red-200 hover:bg-red-50"
                            onClick={() => removeWarehouseAssignment(stock.warehouseId)}
                            title="Remove location"
                            type="button"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="rounded-xl bg-gray-50 p-3 text-xs font-bold text-gray-400">No warehouse locations for this SKU yet.</p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Main Image</h2>
            <p className="mt-1 text-xs font-semibold text-gray-400">This image appears on product cards and first in the detail slider.</p>
            <div className="mt-4 flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
              {form.image ? (
                <img alt="Main product" className="h-full w-full object-contain p-4" src={resolveProductImage(form.image)} />
              ) : (
                <ImagePlus className="h-10 w-10 text-gray-300" />
              )}
            </div>
            <MediaLibraryPicker
              buttonClassName="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
              buttonLabel="Choose main image"
              folder="products"
              onSelect={selectMainImage}
              selectedUrls={form.image ? [form.image] : []}
              source="products"
            />
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-gray-900">Slider Images</h2>
                <p className="mt-1 text-xs font-semibold text-gray-400">Upload multiple images for the product detail carousel.</p>
              </div>
              <MediaLibraryPicker
                buttonClassName="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]"
                buttonLabel="Add"
                folder="products"
                multiple
                onSelect={selectGalleryImages}
                selectedUrls={sliderImages}
                source="products"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {sliderImages.map((image) => (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-2" key={image}>
                  <div className="flex aspect-square items-center justify-center">
                    <img alt="Product slider" className="h-full w-full object-contain" src={resolveProductImage(image)} />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button className="flex-1 rounded-lg bg-gray-950 px-2 py-2 text-[11px] font-black text-white" onClick={() => makeMainImage(image)} type="button">
                      Main
                    </button>
                    <button className="rounded-lg border border-red-100 bg-red-50 px-2 py-2 text-red-600" onClick={() => removeImage(image)} type="button">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {!sliderImages.length && <p className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm font-semibold text-gray-400">No slider images yet.</p>}
            </div>
          </section>

          <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-950 px-5 py-4 text-sm font-black text-white disabled:opacity-60" disabled={saving} type="submit">
            <Save size={16} /> {saving ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
          </button>
        </aside>
      </form>
    </div>
  );
}
